import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { tasksAPI, clientsAPI, taskStagesAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../contexts/AuthContext';
import { Search, Clock, User, Plus, GripVertical, Loader2, Building2, Pencil, Trash2, MoreVertical, Settings, Archive, ArchiveRestore } from 'lucide-react';
import { format } from 'date-fns';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import ClientSearchInput from '../components/ClientSearchInput';

// Color options for stages
const COLOR_OPTIONS = [
  { value: 'bg-yellow-100 text-yellow-800', border: 'border-yellow-300', label: 'Yellow' },
  { value: 'bg-blue-100 text-blue-800', border: 'border-blue-300', label: 'Blue' },
  { value: 'bg-purple-100 text-purple-800', border: 'border-purple-300', label: 'Purple' },
  { value: 'bg-green-100 text-green-800', border: 'border-green-300', label: 'Green' },
  { value: 'bg-red-100 text-red-800', border: 'border-red-300', label: 'Red' },
  { value: 'bg-orange-100 text-orange-800', border: 'border-orange-300', label: 'Orange' },
  { value: 'bg-pink-100 text-pink-800', border: 'border-pink-300', label: 'Pink' },
  { value: 'bg-gray-100 text-gray-800', border: 'border-gray-300', label: 'Gray' },
  { value: 'bg-teal-100 text-teal-800', border: 'border-teal-300', label: 'Teal' },
  { value: 'bg-indigo-100 text-indigo-800', border: 'border-indigo-300', label: 'Indigo' },
];

const Tasks = () => {
  const { hasPermission } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [assigneeFilter, setAssigneeFilter] = useState('all');
  const [draggedTask, setDraggedTask] = useState(null);
  const [dragOverStatus, setDragOverStatus] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    clientId: '',
    client: '',
    assignedTo: '',
    dueDate: '',
    priority: 'medium',
    status: 'pending',
    forSpouse: false
  });
  const [selectedClientHasSpouse, setSelectedClientHasSpouse] = useState(false);
  
  // Edit task state
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [editClientHasSpouse, setEditClientHasSpouse] = useState(false);
  
  // Delete confirmation state
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  
  // Archive state
  const [showArchived, setShowArchived] = useState(false);
  const [archiveDialog, setArchiveDialog] = useState(false);
  const [taskToArchive, setTaskToArchive] = useState(null);
  
  // Stage management state
  const [showStagesDialog, setShowStagesDialog] = useState(false);
  const [showAddStageDialog, setShowAddStageDialog] = useState(false);
  const [showEditStageDialog, setShowEditStageDialog] = useState(false);
  const [newStage, setNewStage] = useState({ label: '', color: 'bg-gray-100 text-gray-800', borderColor: 'border-gray-300' });
  const [editStage, setEditStage] = useState(null);
  const [stageToDelete, setStageToDelete] = useState(null);
  const [deleteStageDialog, setDeleteStageDialog] = useState(false);
  const [draggedStage, setDraggedStage] = useState(null);
  const [dragOverStageId, setDragOverStageId] = useState(null);
  
  const navigate = useNavigate();
  const { toast } = useToast();

  const [teamMembers, setTeamMembers] = useState([]);

  useEffect(() => {
    fetchTasks();
    fetchClients();
    fetchStages();
    fetchTeamMembers();
  }, [showArchived]);

  const fetchTeamMembers = async () => {
    try {
      const { teamAPI } = await import('../services/api');
      const response = await teamAPI.getMembers();
      setTeamMembers(response.data.members || []);
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  };

  const fetchStages = async () => {
    try {
      const res = await taskStagesAPI.getAll();
      setStages(res.data.stages || []);
    } catch (error) {
      console.error('Error fetching stages:', error);
      // Fall back to default stages if API fails
      setStages([
        { id: 'pending', label: 'Pending', color: 'bg-yellow-100 text-yellow-800', borderColor: 'border-yellow-300' },
        { id: 'in_progress', label: 'In Progress', color: 'bg-blue-100 text-blue-800', borderColor: 'border-blue-300' },
        { id: 'scheduled', label: 'Scheduled', color: 'bg-purple-100 text-purple-800', borderColor: 'border-purple-300' },
        { id: 'completed', label: 'Completed', color: 'bg-green-100 text-green-800', borderColor: 'border-green-300' }
      ]);
    }
  };

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const response = await tasksAPI.getAll({ include_archived: showArchived });
      setTasks(response.data.tasks || []);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      toast({
        title: 'Error',
        description: 'Failed to load tasks',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await clientsAPI.getAll({});
      setClients(response.data.clients || []);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const handleDragStart = (e, task) => {
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', task._id);
  };

  const handleDragOver = (e, status) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverStatus(status);
  };

  const handleDragLeave = () => {
    setDragOverStatus(null);
  };

  const handleDrop = async (e, newStatus) => {
    e.preventDefault();
    setDragOverStatus(null);
    
    if (!draggedTask || draggedTask.status === newStatus) {
      setDraggedTask(null);
      return;
    }

    try {
      await tasksAPI.update(draggedTask._id, { status: newStatus });
      
      // Update local state
      setTasks(prev => prev.map(task => 
        task._id === draggedTask._id ? { ...task, status: newStatus } : task
      ));

      toast({
        title: 'Task Updated',
        description: `"${draggedTask.title}" moved to ${stages.find(s => s.id === newStatus)?.label}`
      });
      
      // If moved to completed, ask if user wants to archive
      if (newStatus === 'completed') {
        setTaskToArchive(draggedTask);
        setArchiveDialog(true);
      }
    } catch (error) {
      console.error('Error updating task:', error);
      toast({
        title: 'Error',
        description: 'Failed to update task status',
        variant: 'destructive'
      });
    }
    
    setDraggedTask(null);
  };

  const handleArchiveTask = async () => {
    if (!taskToArchive) return;
    
    try {
      await tasksAPI.archive(taskToArchive._id);
      setTasks(prev => prev.filter(task => task._id !== taskToArchive._id));
      toast({
        title: 'Task Archived',
        description: `"${taskToArchive.title}" has been archived`
      });
    } catch (error) {
      console.error('Error archiving task:', error);
      toast({
        title: 'Error',
        description: 'Failed to archive task',
        variant: 'destructive'
      });
    } finally {
      setArchiveDialog(false);
      setTaskToArchive(null);
    }
  };

  const handleUnarchiveTask = async (task) => {
    try {
      await tasksAPI.unarchive(task._id);
      fetchTasks();
      toast({
        title: 'Task Restored',
        description: `"${task.title}" has been restored`
      });
    } catch (error) {
      console.error('Error unarchiving task:', error);
      toast({
        title: 'Error',
        description: 'Failed to restore task',
        variant: 'destructive'
      });
    }
  };

  const handleCreateTask = async () => {
    if (!newTask.title || !newTask.clientId || !newTask.assignedTo || !newTask.dueDate) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields',
        variant: 'destructive'
      });
      return;
    }

    try {
      const taskData = {
        ...newTask,
        dueDate: new Date(newTask.dueDate).toISOString(),
        tags: []
      };
      
      await tasksAPI.create(taskData);
      
      toast({
        title: 'Task Created',
        description: `"${newTask.title}" has been created`
      });
      
      setShowCreateDialog(false);
      setNewTask({
        title: '',
        description: '',
        clientId: '',
        client: '',
        assignedTo: '',
        dueDate: '',
        priority: 'medium',
        status: 'pending'
      });
      
      fetchTasks();
    } catch (error) {
      console.error('Error creating task:', error);
      toast({
        title: 'Error',
        description: 'Failed to create task',
        variant: 'destructive'
      });
    }
  };

  const openEditDialog = (task) => {
    setEditTask({
      ...task,
      dueDate: task.dueDate ? task.dueDate.split('T')[0] : ''
    });
    setShowEditDialog(true);
  };

  const handleEditTask = async () => {
    if (!editTask.title || !editTask.clientId || !editTask.assignedTo || !editTask.dueDate) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields',
        variant: 'destructive'
      });
      return;
    }

    try {
      const taskData = {
        ...editTask,
        dueDate: new Date(editTask.dueDate).toISOString()
      };
      
      await tasksAPI.update(editTask._id, taskData);
      
      toast({
        title: 'Task Updated',
        description: `"${editTask.title}" has been updated`
      });
      
      setShowEditDialog(false);
      setEditTask(null);
      fetchTasks();
    } catch (error) {
      console.error('Error updating task:', error);
      toast({
        title: 'Error',
        description: 'Failed to update task',
        variant: 'destructive'
      });
    }
  };

  const openDeleteDialog = (task) => {
    setTaskToDelete(task);
    setDeleteDialog(true);
  };

  const handleDeleteTask = async () => {
    if (!taskToDelete) return;
    
    try {
      await tasksAPI.delete(taskToDelete._id);
      toast({
        title: 'Task Deleted',
        description: `"${taskToDelete.title}" has been deleted`
      });
      setDeleteDialog(false);
      setTaskToDelete(null);
      fetchTasks();
    } catch (error) {
      console.error('Error deleting task:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete task',
        variant: 'destructive'
      });
    }
  };

  const handleClientSelect = (clientId) => {
    const client = clients.find(c => c._id === clientId);
    setNewTask(prev => ({
      ...prev,
      clientId,
      client: client?.name || '',
      forSpouse: false  // Reset when changing client
    }));
    setSelectedClientHasSpouse(!!client?.spouse?.name);
  };

  const handleEditClientSelect = (clientId) => {
    const client = clients.find(c => c._id === clientId);
    setEditTask(prev => ({
      ...prev,
      clientId,
      client: client?.name || ''
    }));
    setEditClientHasSpouse(!!client?.spouse?.name);
  };

  // Stage management functions
  const handleAddStage = async () => {
    if (!newStage.label.trim()) {
      toast({ title: 'Error', description: 'Stage name is required', variant: 'destructive' });
      return;
    }
    try {
      await taskStagesAPI.create(newStage);
      toast({ title: 'Success', description: 'Stage added successfully' });
      setNewStage({ label: '', color: 'bg-gray-100 text-gray-800', borderColor: 'border-gray-300' });
      setShowAddStageDialog(false);
      fetchStages();
    } catch (error) {
      console.error('Error adding stage:', error);
      toast({ title: 'Error', description: 'Failed to add stage', variant: 'destructive' });
    }
  };

  const handleUpdateStage = async () => {
    if (!editStage?.label?.trim()) {
      toast({ title: 'Error', description: 'Stage name is required', variant: 'destructive' });
      return;
    }
    try {
      await taskStagesAPI.update(editStage._id || editStage.id, {
        label: editStage.label,
        color: editStage.color,
        borderColor: editStage.borderColor
      });
      toast({ title: 'Success', description: 'Stage updated successfully' });
      setShowEditStageDialog(false);
      setEditStage(null);
      fetchStages();
    } catch (error) {
      console.error('Error updating stage:', error);
      toast({ title: 'Error', description: 'Failed to update stage', variant: 'destructive' });
    }
  };

  const handleDeleteStage = async () => {
    if (!stageToDelete) return;
    try {
      await taskStagesAPI.delete(stageToDelete._id || stageToDelete.id);
      toast({ title: 'Success', description: 'Stage deleted successfully' });
      setDeleteStageDialog(false);
      setStageToDelete(null);
      fetchStages();
    } catch (error) {
      console.error('Error deleting stage:', error);
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to delete stage', 
        variant: 'destructive' 
      });
    }
  };

  const openEditStageDialog = (stage) => {
    setEditStage({ ...stage });
    setShowEditStageDialog(true);
  };

  const openDeleteStageDialog = (stage) => {
    setStageToDelete(stage);
    setDeleteStageDialog(true);
  };

  // Stage drag and drop handlers
  const handleStageDragStart = (e, stage) => {
    setDraggedStage(stage);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleStageDragOver = (e, stage) => {
    e.preventDefault();
    if (draggedStage && stage._id !== draggedStage._id) {
      setDragOverStageId(stage._id || stage.id);
    }
  };

  const handleStageDragLeave = () => {
    setDragOverStageId(null);
  };

  const handleStageDrop = async (e, targetStage) => {
    e.preventDefault();
    if (!draggedStage || draggedStage._id === targetStage._id) {
      setDraggedStage(null);
      setDragOverStageId(null);
      return;
    }

    // Calculate new order
    const currentStages = [...stages];
    const draggedIndex = currentStages.findIndex(s => (s._id || s.id) === (draggedStage._id || draggedStage.id));
    const targetIndex = currentStages.findIndex(s => (s._id || s.id) === (targetStage._id || targetStage.id));

    // Remove dragged item and insert at target position
    const [removed] = currentStages.splice(draggedIndex, 1);
    currentStages.splice(targetIndex, 0, removed);

    // Update order values
    const reorderedStages = currentStages.map((stage, index) => ({
      id: stage._id || stage.id,
      order: index
    }));

    // Optimistically update UI
    setStages(currentStages.map((stage, index) => ({ ...stage, order: index })));
    setDraggedStage(null);
    setDragOverStageId(null);

    // Persist to backend
    try {
      await taskStagesAPI.reorder(reorderedStages);
      toast({ title: 'Success', description: 'Stages reordered' });
    } catch (error) {
      console.error('Error reordering stages:', error);
      toast({ title: 'Error', description: 'Failed to reorder stages', variant: 'destructive' });
      fetchStages(); // Revert on error
    }
  };

  const handleStageDragEnd = () => {
    setDraggedStage(null);
    setDragOverStageId(null);
  };

  const filteredTasks = tasks.filter(task => {
    const matchesSearch = task.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         task.client?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesPriority = priorityFilter === 'all' || task.priority === priorityFilter;
    const matchesAssignee = assigneeFilter === 'all' || task.assignedTo === assigneeFilter;
    return matchesSearch && matchesPriority && matchesAssignee;
  });

  const groupedTasks = stages.reduce((acc, status) => {
    acc[status.id] = filteredTasks.filter(t => t.status === status.id);
    return acc;
  }, {});

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'destructive';
      case 'medium': return 'default';
      case 'low': return 'secondary';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900" data-testid="tasks-title">Tasks</h1>
          <p className="text-gray-500 mt-1">Manage and track all your tasks</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowStagesDialog(true)} className="gap-2" data-testid="manage-stages-btn">
            <Settings className="h-4 w-4" />
            Manage Stages
          </Button>
          <Button onClick={() => setShowCreateDialog(true)} className="gap-2" data-testid="create-task-btn">
            <Plus className="h-4 w-4" />
            Create New Task
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-4 flex-wrap items-center">
            <div className="flex-1 min-w-[250px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search tasks..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="task-search-input"
                />
              </div>
            </div>
            
            {/* Priority Filter */}
            <div className="flex gap-2">
              <Button
                variant={priorityFilter === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPriorityFilter('all')}
              >
                All Priorities
              </Button>
              <Button
                variant={priorityFilter === 'high' ? 'destructive' : 'outline'}
                size="sm"
                onClick={() => setPriorityFilter(priorityFilter === 'high' ? 'all' : 'high')}
              >
                High
              </Button>
              <Button
                variant={priorityFilter === 'medium' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPriorityFilter(priorityFilter === 'medium' ? 'all' : 'medium')}
              >
                Medium
              </Button>
              <Button
                variant={priorityFilter === 'low' ? 'secondary' : 'outline'}
                size="sm"
                onClick={() => setPriorityFilter(priorityFilter === 'low' ? 'all' : 'low')}
              >
                Low
              </Button>
            </div>
            
            {/* Team Member Filter */}
            <div className="flex gap-2 items-center">
              <span className="text-sm text-gray-500">|</span>
              <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
                <SelectTrigger className="w-[160px]" data-testid="assignee-filter">
                  <User className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Team Member" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Members</SelectItem>
                  {teamMembers.map((member) => (
                    <SelectItem key={member._id} value={member._id}>{member.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Show Archived Toggle */}
            <div className="flex items-center gap-2 ml-auto">
              <Switch
                id="show-archived"
                checked={showArchived}
                onCheckedChange={setShowArchived}
              />
              <Label htmlFor="show-archived" className="text-sm cursor-pointer flex items-center gap-1">
                <Archive className="h-4 w-4" />
                {showArchived ? 'Showing Archived' : 'Show Archived'}
              </Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Kanban Board */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="kanban-board">
        {stages.map((status) => (
          <div 
            key={status.id}
            className={`space-y-3 p-4 rounded-lg bg-gray-50 min-h-[500px] transition-all ${
              dragOverStatus === status.id ? `ring-2 ring-blue-400 ${status.borderColor}` : ''
            }`}
            onDragOver={(e) => handleDragOver(e, status.id)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, status.id)}
            data-testid={`kanban-column-${status.id}`}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">{status.label}</h3>
              <Badge className={status.color}>{groupedTasks[status.id]?.length || 0}</Badge>
            </div>
            
            <div className="space-y-3">
              {groupedTasks[status.id]?.map((task) => (
                <Card 
                  key={task._id} 
                  className={`hover:shadow-md transition-all cursor-grab active:cursor-grabbing ${
                    draggedTask?._id === task._id ? 'opacity-50 scale-95' : ''
                  }`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, task)}
                  data-testid={`task-card-${task._id}`}
                >
                  <CardContent className="p-4">
                    <div className="space-y-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <GripVertical className="h-4 w-4 text-gray-400" />
                            <Badge variant={getPriorityColor(task.priority)} className="text-xs">
                              {task.priority}
                            </Badge>
                            {task.forSpouse && (
                              <Badge className="bg-pink-100 text-pink-800 text-xs">Spouse</Badge>
                            )}
                            {task.isArchived && (
                              <Badge className="bg-gray-100 text-gray-600 text-xs">Archived</Badge>
                            )}
                          </div>
                          <h4 className="font-medium text-gray-900 text-sm">{task.title}</h4>
                          {task.description && (
                            <p className="text-xs text-gray-500 mt-1 line-clamp-2">{task.description}</p>
                          )}
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openEditDialog(task); }}>
                              <Pencil className="h-4 w-4 mr-2" />
                              Edit Task
                            </DropdownMenuItem>
                            {task.isArchived ? (
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleUnarchiveTask(task); }}>
                                <ArchiveRestore className="h-4 w-4 mr-2" />
                                Restore Task
                              </DropdownMenuItem>
                            ) : (
                              task.status === 'completed' && (
                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); setTaskToArchive(task); setArchiveDialog(true); }}>
                                  <Archive className="h-4 w-4 mr-2" />
                                  Archive Task
                                </DropdownMenuItem>
                              )
                            )}
                            {hasPermission('canDeleteTask') && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  onClick={(e) => { e.stopPropagation(); openDeleteDialog(task); }}
                                  className="text-red-600"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete Task
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      
                      <div className="space-y-2 text-xs">
                        <div className="flex items-center gap-1.5 text-gray-600">
                          <Building2 className="h-3 w-3" />
                          <span 
                            className="truncate hover:text-blue-600 cursor-pointer"
                            onClick={() => {
                              const client = clients.find(c => c._id === task.clientId);
                              if (client) navigate(`/clients/${client._id}`);
                            }}
                          >
                            {task.client}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 text-gray-600">
                          <Clock className="h-3 w-3" />
                          <span>Due {format(new Date(task.dueDate), 'MMM dd, yyyy')}</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-gray-600">
                          <User className="h-3 w-3" />
                          <span>{task.assignedTo}</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              {groupedTasks[status.id]?.length === 0 && (
                <div className="text-center py-8 text-gray-400 text-sm">
                  No tasks in {status.label.toLowerCase()}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Create Task Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New Task</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                placeholder="Task title"
                value={newTask.title}
                onChange={(e) => setNewTask(prev => ({ ...prev, title: e.target.value }))}
                data-testid="new-task-title"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Task description"
                value={newTask.description}
                onChange={(e) => setNewTask(prev => ({ ...prev, description: e.target.value }))}
                data-testid="new-task-description"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="client">Client *</Label>
              <ClientSearchInput
                value={newTask.clientId}
                onSelect={(clientId, clientName, client) => {
                  setNewTask(prev => ({ 
                    ...prev, 
                    clientId, 
                    client: clientName 
                  }));
                  setSelectedClientHasSpouse(!!client?.spouse?.name);
                }}
                placeholder="Search clients..."
                data-testid="new-task-client"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="assignedTo">Assigned To *</Label>
              <Select 
                value={newTask.assignedTo} 
                onValueChange={(value) => setNewTask(prev => ({ ...prev, assignedTo: value }))}
              >
                <SelectTrigger data-testid="new-task-assigned">
                  <SelectValue placeholder="Select team member" />
                </SelectTrigger>
                <SelectContent>
                  {teamMembers.map((member) => (
                    <SelectItem key={member._id} value={member._id}>{member.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="dueDate">Due Date *</Label>
                <Input
                  id="dueDate"
                  type="date"
                  value={newTask.dueDate}
                  onChange={(e) => setNewTask(prev => ({ ...prev, dueDate: e.target.value }))}
                  data-testid="new-task-duedate"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="priority">Priority</Label>
                <Select 
                  value={newTask.priority} 
                  onValueChange={(value) => setNewTask(prev => ({ ...prev, priority: value }))}
                >
                  <SelectTrigger data-testid="new-task-priority">
                    <SelectValue placeholder="Select priority" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* For Spouse Checkbox - only show if client has spouse */}
            {selectedClientHasSpouse && (
              <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <input
                  type="checkbox"
                  id="forSpouse"
                  checked={newTask.forSpouse}
                  onChange={(e) => setNewTask(prev => ({ ...prev, forSpouse: e.target.checked }))}
                  className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  data-testid="new-task-for-spouse"
                />
                <Label htmlFor="forSpouse" className="text-sm cursor-pointer">
                  This task is for the <strong>spouse</strong>
                </Label>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="status">Initial Status</Label>
              <Select 
                value={newTask.status} 
                onValueChange={(value) => setNewTask(prev => ({ ...prev, status: value }))}
              >
                <SelectTrigger data-testid="new-task-status">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {stages.map((status) => (
                    <SelectItem key={status.id} value={status.id}>
                      {status.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex gap-2 pt-4">
              <Button onClick={handleCreateTask} className="flex-1" data-testid="submit-new-task">
                Create Task
              </Button>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Task Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Task</DialogTitle>
          </DialogHeader>
          {editTask && (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input
                  placeholder="Task title"
                  value={editTask.title}
                  onChange={(e) => setEditTask(prev => ({ ...prev, title: e.target.value }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  placeholder="Task description..."
                  value={editTask.description || ''}
                  onChange={(e) => setEditTask(prev => ({ ...prev, description: e.target.value }))}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Client *</Label>
                <Select value={editTask.clientId} onValueChange={handleEditClientSelect}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select client" />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((client) => (
                      <SelectItem key={client._id} value={client._id}>
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Assigned To *</Label>
                <Select value={editTask.assignedTo} onValueChange={(v) => setEditTask(prev => ({ ...prev, assignedTo: v }))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select team member" />
                  </SelectTrigger>
                  <SelectContent>
                    {teamMembers.map((member) => (
                      <SelectItem key={member._id} value={member._id}>{member.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Due Date *</Label>
                  <Input
                    type="date"
                    value={editTask.dueDate}
                    onChange={(e) => setEditTask(prev => ({ ...prev, dueDate: e.target.value }))}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Priority</Label>
                  <Select value={editTask.priority} onValueChange={(v) => setEditTask(prev => ({ ...prev, priority: v }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={editTask.status} onValueChange={(v) => setEditTask(prev => ({ ...prev, status: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {stages.map((status) => (
                      <SelectItem key={status.id} value={status.id}>{status.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex gap-2 pt-4">
                <Button onClick={handleEditTask} className="flex-1">Save Changes</Button>
                <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Task Dialog */}
      <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Task</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-600">
              Are you sure you want to delete "<strong>{taskToDelete?.title}</strong>"? This action cannot be undone.
            </p>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setDeleteDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteTask}>Delete Task</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manage Stages Dialog */}
      <Dialog open={showStagesDialog} onOpenChange={setShowStagesDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Manage Task Stages</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-500">Add, edit or remove task stages</p>
              <Button size="sm" onClick={() => setShowAddStageDialog(true)} className="gap-1" data-testid="add-stage-btn">
                <Plus className="h-4 w-4" />
                Add Stage
              </Button>
            </div>
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {stages.map((stage, index) => (
                <div 
                  key={stage._id || stage.id}
                  draggable
                  onDragStart={(e) => handleStageDragStart(e, stage)}
                  onDragOver={(e) => handleStageDragOver(e, stage)}
                  onDragLeave={handleStageDragLeave}
                  onDrop={(e) => handleStageDrop(e, stage)}
                  onDragEnd={handleStageDragEnd}
                  className={`flex items-center justify-between p-3 border rounded-lg cursor-move transition-all ${stage.borderColor} ${
                    dragOverStageId === (stage._id || stage.id) ? 'ring-2 ring-blue-500 bg-blue-50' : ''
                  } ${draggedStage?._id === stage._id ? 'opacity-50' : ''}`}
                >
                  <div className="flex items-center gap-3">
                    <GripVertical className="h-4 w-4 text-gray-400" />
                    <Badge className={stage.color}>{stage.label}</Badge>
                    <span className="text-sm text-gray-500">
                      ({filteredTasks.filter(t => t.status === stage.id).length} tasks)
                    </span>
                  </div>
                  <div className="flex gap-1">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8"
                      onClick={() => openEditStageDialog(stage)}
                      data-testid={`edit-stage-${stage.id}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8 text-red-500 hover:text-red-700"
                      onClick={() => openDeleteStageDialog(stage)}
                      data-testid={`delete-stage-${stage.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStagesDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Stage Dialog */}
      <Dialog open={showAddStageDialog} onOpenChange={setShowAddStageDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Stage</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label>Stage Name *</Label>
              <Input
                value={newStage.label}
                onChange={(e) => setNewStage({ ...newStage, label: e.target.value })}
                placeholder="Enter stage name..."
                data-testid="new-stage-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Color</Label>
              <div className="grid grid-cols-5 gap-2">
                {COLOR_OPTIONS.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    className={`p-3 rounded-lg border-2 transition-all ${color.value} ${
                      newStage.color === color.value ? 'ring-2 ring-offset-2 ring-blue-500' : ''
                    }`}
                    onClick={() => setNewStage({ ...newStage, color: color.value, borderColor: color.border })}
                  >
                    <span className="sr-only">{color.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="p-3 border rounded-lg">
              <p className="text-xs text-gray-500 mb-2">Preview:</p>
              <Badge className={newStage.color}>{newStage.label || 'Stage Name'}</Badge>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddStageDialog(false)}>Cancel</Button>
            <Button onClick={handleAddStage} data-testid="save-new-stage-btn">Add Stage</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Stage Dialog */}
      <Dialog open={showEditStageDialog} onOpenChange={setShowEditStageDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Stage</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label>Stage Name *</Label>
              <Input
                value={editStage?.label || ''}
                onChange={(e) => setEditStage({ ...editStage, label: e.target.value })}
                placeholder="Enter stage name..."
                data-testid="edit-stage-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Color</Label>
              <div className="grid grid-cols-5 gap-2">
                {COLOR_OPTIONS.map((color) => (
                  <button
                    key={color.value}
                    type="button"
                    className={`p-3 rounded-lg border-2 transition-all ${color.value} ${
                      editStage?.color === color.value ? 'ring-2 ring-offset-2 ring-blue-500' : ''
                    }`}
                    onClick={() => setEditStage({ ...editStage, color: color.value, borderColor: color.border })}
                  >
                    <span className="sr-only">{color.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="p-3 border rounded-lg">
              <p className="text-xs text-gray-500 mb-2">Preview:</p>
              <Badge className={editStage?.color}>{editStage?.label || 'Stage Name'}</Badge>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditStageDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateStage} data-testid="save-edit-stage-btn">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Stage Confirmation */}
      <Dialog open={deleteStageDialog} onOpenChange={setDeleteStageDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete Stage</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-600">
              Are you sure you want to delete the stage <strong>{stageToDelete?.label}</strong>?
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Note: You cannot delete a stage that has tasks assigned to it. Move the tasks to another stage first.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteStageDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteStage} data-testid="confirm-delete-stage-btn">Delete Stage</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive Task Confirmation */}
      <AlertDialog open={archiveDialog} onOpenChange={setArchiveDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive Completed Task?</AlertDialogTitle>
            <AlertDialogDescription>
              Would you like to archive <strong>"{taskToArchive?.title}"</strong>? 
              Archived tasks are hidden from the main view but can be accessed by toggling "Show Archived".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setArchiveDialog(false); setTaskToArchive(null); }}>
              Keep in View
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleArchiveTask} className="bg-gray-600 hover:bg-gray-700">
              <Archive className="h-4 w-4 mr-2" />
              Archive Task
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Tasks;
